import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  CircularProgress
} from '@mui/material';
import LifePlanCompletionStats from './LifePlanCompletionStats';
import { PaceReport } from '../../components/PaceReport';
import { auth } from '../../services/api';

type ReportType = 'progress' | 'pace';

interface Organization {
  id: string;
  name: string;
  org_id: string;
}

const AdminReports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('progress');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const response = await auth.getOrganizations();
        setOrganizations(response.organizations);
        if (response.organizations.length > 0) {
          setSelectedOrganization(response.organizations[0].id);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleReportTypeChange = (event: SelectChangeEvent) => {
    setReportType(event.target.value as ReportType);
  };

  const handleOrganizationChange = (event: SelectChangeEvent) => {
    setSelectedOrganization(event.target.value);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || organizations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{error || 'No organizations available'}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ mr: 2 }}>
          Admin Reports
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200, mr: 2 }}>
          <InputLabel id="report-type-label">Report Type</InputLabel>
          <Select
            labelId="report-type-label"
            value={reportType}
            onChange={handleReportTypeChange}
            label="Report Type"
            inputProps={{ 'aria-label': 'Report type' }}
          >
            <MenuItem value="progress">Life Plan Completion Stats</MenuItem>
            <MenuItem value="pace">User Pace Analysis</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="organization-label">Organization</InputLabel>
          <Select
            labelId="organization-label"
            value={selectedOrganization}
            onChange={handleOrganizationChange}
            label="Organization"
            inputProps={{ 'aria-label': 'Organization' }}
          >
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={{ mb: 3 }} />
      
      {reportType === 'progress' ? (
        <LifePlanCompletionStats organizationId={selectedOrganization} />
      ) : (
        <Box>
          <Typography variant="h5" gutterBottom>
            User Pace Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This report shows the median and 90th percentile response times for different question paths.
          </Typography>
          <PaceReport organizationId={selectedOrganization} />
        </Box>
      )}
    </Container>
  );
};

export default AdminReports;
